/** One-off contact review. Device picker access needs a fresh user action; raw contacts
 * never enter app state/storage. Only reviewed email addresses reach the matching endpoint. */
export function createContactReview(ctx, { validateContactBatch, contactInvitationURL }, env = {}) {
  const nav = env.navigator ?? globalThis.navigator;
  const location = env.location ?? globalThis.location;
  const secure = env.secure ?? globalThis.isSecureContext;
  const topLevel = env.topLevel ?? globalThis.window?.top === globalThis.window?.self;
  function openContacts() {
    const actor = ctx.state.me.id;
    let disposed = false,
      pending = false;
    const form = ctx.e('form', 'form-stack');
    const status = ctx.e('p', 'subtle');
    status.setAttribute('role', 'status');
    const emails = ctx.field('textarea', {
      maxLength: 6000,
      rows: 4,
      placeholder: ctx.uiText('One email address per line'),
      ariaLabel: ctx.uiText('Selected email addresses'),
    });
    const results = ctx.e('div', 'contact-results');
    const review = ctx.e('button', 'primary-button', ctx.uiText('Review and find friends'));
    form.append(
      ctx.iconButton('search', 'Search public names and handles', () => {
        ctx.modal.close();
        ctx.query = '';
        const draft = globalThis.document?.querySelector?.('#search');
        if (draft) draft.value = '';
        ctx.go('search');
      }),
      ctx.e(
        'p',
        'subtle',
        ctx.uiText(
          'Choose contacts on your device or enter up to twenty emails. Review them before matching. Only verified, opted-in accounts can appear.',
        ),
      ),
    );
    const supported = secure && topLevel && typeof nav?.contacts?.select === 'function';
    if (supported)
      form.append(
        ctx.iconButton('users', 'Choose device contacts', async () => {
          try {
            const chosen = await nav.contacts.select(['email'], { multiple: true });
            if (disposed || ctx.state.me.id !== actor) return;
            const values = chosen.flatMap((contact) =>
              Array.isArray(contact.email) ? contact.email : [],
            );
            if (!values.length) {
              status.textContent = ctx.uiText(
                'No email addresses selected. You can enter emails instead.',
              );
              return;
            }
            emails.value = validateContactBatch({ emails: values, reviewed: true }).join('\n');
            status.textContent = ctx.uiText(
              'Review the selected emails below. Nothing has been sent for matching yet.',
            );
            emails.focus();
          } catch {
            if (!disposed)
              status.textContent = ctx.uiText(
                'Contact selection was canceled or unavailable. You can enter emails instead.',
              );
          }
        }),
      );
    else
      form.append(
        ctx.e(
          'p',
          'subtle',
          ctx.uiText(
            'Device contact selection is unavailable in this browser. Enter emails manually.',
          ),
        ),
      );
    form.append(
      ctx.e('label', '', ctx.uiText('Selected email addresses')),
      emails,
      review,
      status,
      results,
    );
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (pending || disposed) return;
      let reviewed;
      try {
        reviewed = validateContactBatch({
          emails: emails.value
            .split(/[\n,;]+/)
            .map((email) => email.trim())
            .filter(Boolean),
          reviewed: true,
        });
      } catch (error) {
        status.textContent = ctx.uiText(error.message);
        return;
      }
      pending = true;
      review.disabled = true;
      status.textContent = ctx.uiText('Finding opted-in friends…');
      results.replaceChildren();
      try {
        const data = await ctx.api('/api/contacts/match', { emails: reviewed, reviewed: true });
        if (disposed || ctx.state.me.id !== actor) return;
        status.textContent = ctx.uiText(
          'Matching complete. Unmatched addresses may belong to accounts that are private or unavailable.',
        );
        for (const match of data.matches) {
          const user = match.user;
          const row = ctx.append(
            ctx.e('div', 'contact-match'),
            ctx.avatar(user),
            ctx.link(user.name, `#/profile/${user.id}`),
          );
          if (ctx.state.friends.includes(user.id))
            row.append(ctx.e('small', 'subtle', ctx.uiText('Already friends')));
          else if (
            ctx.state.friendRequests.some(
              (request) =>
                request.status === 'pending' &&
                ((request.from === actor && request.to === user.id) ||
                  (request.to === actor && request.from === user.id)),
            )
          )
            row.append(ctx.e('small', 'subtle', ctx.uiText('Friend request pending')));
          else
            row.append(
              ctx.button('Request friendship', async () => {
                await ctx.command(
                  { type: 'friend', id: user.id, requestOnly: true },
                  { paint: false },
                );
                row.replaceChildren(
                  ctx.avatar(user),
                  ctx.link(user.name, `#/profile/${user.id}`),
                  ctx.e('small', 'subtle', ctx.uiText('Friend request pending')),
                );
              }),
            );
          results.append(row);
        }
        if (!data.matches.length)
          results.append(ctx.e('p', 'subtle', ctx.uiText('No discoverable matches.')));
        results.append(
          ctx.e(
            'p',
            'subtle',
            ctx.uiText(
              'Email invitations open a draft in your email app. Review and send there; no email is sent by myAudience.',
            ),
          ),
        );
        if (['localhost', '127.0.0.1'].includes(location.hostname))
          results.append(
            ctx.e(
              'p',
              'subtle',
              ctx.uiText('This local demo invitation link only works on this Mac.'),
            ),
          );
        for (const index of data.unmatched) {
          const email = reviewed[index];
          const invitation = ctx.link(
            ctx.uiText('Open email invitation draft'),
            contactInvitationURL(email, ctx.state.me.name, `${location.origin}/#/profile/${actor}`),
            'secondary-button',
          );
          invitation.setAttribute(
            'aria-label',
            `${ctx.uiText('Open email invitation draft')}: ${email}`,
          );
          results.append(
            ctx.append(ctx.e('div', 'contact-match'), ctx.e('span', '', email), invitation),
          );
        }
      } catch (error) {
        if (!disposed) status.textContent = ctx.uiText(error.message);
      } finally {
        pending = false;
        review.disabled = false;
      }
    });
    ctx.modal.addEventListener(
      'close',
      () => {
        disposed = true;
        emails.value = '';
        results.replaceChildren();
      },
      { once: true },
    );
    ctx.dialog('Find friends from contacts', form);
    emails.focus({ preventScroll: true });
  }
  return { openContacts };
}
